// The freeze rule every committed workbook state goes through, without the editor's dependencies,
// so the parse path can freeze cells as they arrive from the worker.

const isPlainContainer = (value: unknown): value is object => {
    if (!value || typeof value !== 'object' || Object.isFrozen(value)) return false;
    if (Array.isArray(value)) return true;
    const proto = Object.getPrototypeOf(value);
    return proto === null || proto === Object.prototype;
};

// Reused across calls: this runs once per cell on a hydrating sheet.
const freezeStack: unknown[] = [];
const freezeOrder: object[] = [];

const pushOpen = (child: unknown) => {
    if (child !== null && typeof child === 'object' && !Object.isFrozen(child)) freezeStack.push(child);
};

/** The react patch's `__aicaDeepFreeze` rule (plain objects and arrays, frozen subtrees skipped),
 *  which every committed workbook state goes through. Freezing ahead of a commit leaves the
 *  committed state the same and lets immer skip the subtree instead of walking it. */
export function deepFreezePlain<T>(root: T): T {
    if (!isPlainContainer(root)) return root;
    freezeStack.push(root);
    while (freezeStack.length) {
        const value = freezeStack.pop();
        if (!isPlainContainer(value)) continue;
        freezeOrder.push(value);
        if (Array.isArray(value)) {
            for (let i = 0; i < value.length; i++) pushOpen(value[i]);
        } else {
            const record = value as Record<string, unknown>;
            Object.keys(record).forEach((key) => pushOpen(record[key]));
        }
    }
    // Children before parents, as the patch does.
    for (let i = freezeOrder.length - 1; i >= 0; i--) Object.freeze(freezeOrder[i]);
    freezeOrder.length = 0;
    return root;
}
